import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, NotebookPen, Sparkles, Languages, ArrowRight } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { trackCTA } from "@/lib/gtag";

const pillars = [
  { key: "notes", icon: NotebookPen, href: "/live-notes", color: "text-emerald-400", bg: "bg-emerald-500/10", soon: false },
  { key: "agent", icon: Sparkles, href: "/live-agent", color: "text-purple-400", bg: "bg-purple-500/10", soon: true },
  { key: "translator", icon: Languages, href: "/live-translator", color: "text-cyan-400", bg: "bg-cyan-500/10", soon: false },
];

export function Navigation() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/40">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link href="/">
          <div className="flex items-center gap-2 cursor-pointer">
            <img src="/traverba.svg" alt="Traverba" className="w-8 h-8" />
            <span className="text-xl font-bold tracking-tight italic" style={{ fontFamily: "Arial, sans-serif" }}>TRAVERBA</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {/* Features mega-menu */}
          <div
            className="relative"
            onMouseEnter={() => setFeaturesOpen(true)}
            onMouseLeave={() => setFeaturesOpen(false)}
          >
            <Link
              href="/features"
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${featuresOpen ? "text-primary" : "hover:text-primary"}`}
            >
              {t('nav.features')}
              <ChevronDown className={`w-4 h-4 transition-transform ${featuresOpen ? "rotate-180 text-primary" : ""}`} />
            </Link>

            {featuresOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="w-[380px] rounded-2xl border border-white/10 bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/50 p-2.5"
                >
                  <div className="px-2.5 pt-2 pb-1 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">
                    {t('rb.nav.exploreFeatures')}
                  </div>
                  {pillars.map((p) => (
                    <Link
                      key={p.key}
                      href={p.href}
                      onClick={() => setFeaturesOpen(false)}
                      className="flex items-center gap-3 rounded-xl p-3 hover:bg-white/5 transition-colors group"
                    >
                      <div className={`w-10 h-10 rounded-xl ${p.bg} flex items-center justify-center shrink-0`}>
                        <p.icon className={`w-5 h-5 ${p.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm font-display">{t(`rb.pillarNames.${p.key}`)}</span>
                          {p.soon && (
                            <span className="text-[10px] font-semibold text-purple-300 bg-purple-500/15 rounded-full px-2 py-0.5">
                              {t('rb.nav.soon')}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{t(`rb.nav.items.${p.key}.desc`)}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform shrink-0" />
                    </Link>
                  ))}
                </motion.div>
              </div>
            )}
          </div>

          <Link href="/pricing" className="text-sm font-medium hover:text-primary transition-colors">{t('nav.pricing')}</Link>
          <Link href="/blog" className="text-sm font-medium hover:text-primary transition-colors">{t('nav.blog')}</Link>
          <Link href="/enterprise" className="text-sm font-medium hover:text-primary transition-colors">{t('nav.enterprise')}</Link>
          <Link href="/about" className="text-sm font-medium hover:text-primary transition-colors">{t('nav.about')}</Link>
          <LanguageSwitcher />
          <Button
            variant="default"
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_15px_rgba(6,182,212,0.5)] transition-all hover:shadow-[0_0_25px_rgba(6,182,212,0.7)]"
            asChild
          >
            <Link href="/download" onClick={() => trackCTA("download_cta", "Nav Download Free")}>
              {t('nav.download')}
            </Link>
          </Button>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          <button
            className="text-foreground"
            onClick={toggleMenu}
            aria-label={t('nav.menu', 'Menu')}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden absolute top-full left-0 right-0 bg-background border-b border-border p-4 flex flex-col gap-4"
        >
          {/* Features pillars */}
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground px-1 uppercase">{t('rb.nav.featuresLabel')}</span>
            {pillars.map((p) => (
              <Link
                key={p.key}
                href={p.href}
                className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-white/5 transition-colors"
                onClick={toggleMenu}
              >
                <div className={`w-9 h-9 rounded-lg ${p.bg} flex items-center justify-center shrink-0`}>
                  <p.icon className={`w-5 h-5 ${p.color}`} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm font-display">{t(`rb.pillarNames.${p.key}`)}</span>
                  {p.soon && (
                    <span className="text-[10px] font-semibold text-purple-300 bg-purple-500/15 rounded-full px-2 py-0.5">
                      {t('rb.nav.soon')}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>

          <Link href="/pricing" className="text-sm font-medium hover:text-primary" onClick={toggleMenu}>{t('nav.pricing')}</Link>
          <Link href="/blog" className="text-sm font-medium hover:text-primary" onClick={toggleMenu}>{t('nav.blog')}</Link>
          <Link href="/enterprise" className="text-sm font-medium hover:text-primary" onClick={toggleMenu}>{t('nav.enterprise')}</Link>
          <Link href="/about" className="text-sm font-medium hover:text-primary" onClick={toggleMenu}>{t('nav.about')}</Link>
          <Button className="w-full" asChild>
            <Link href="/download" onClick={() => { trackCTA("download_cta", "Mobile Nav Download Free"); toggleMenu(); }}>
              {t('nav.download')}
            </Link>
          </Button>
        </motion.div>
      )}
    </nav>
  );
}
